import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Lock, Shield, Moon, Sun } from 'lucide-react';
import { Button } from '../components/ui';
import { useTheme } from '../contexts/ThemeContext';
import { customToast } from '../utils/customToast';
import { authService } from '../services/authService';

const ForgotPassword: React.FC = () => {
  const [step, setStep] = useState<'email' | 'code' | 'password'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(0);
  const [userName, setUserName] = useState('');
  
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  // Timer countdown
  React.useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await authService.sendResetCode(email);
      
      if (response.success) {
        customToast.success(response.message || 'Verification code sent to your email!');
        
        // Store user name from response
        if (response.data?.user_name) {
          setUserName(response.data.user_name);
        }
        
        setStep('code');
        setTimer(900); // 15 minutes in seconds
      } else {
        customToast.error(response.message || 'Failed to send verification code');
      }
    } catch (error) {
      customToast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (code.length !== 6) {
      customToast.error('Please enter the complete 6-digit code');
      return;
    }

    setLoading(true);

    try {
      const response = await authService.verifyResetCode(email, code);
      
      if (response.success) {
        customToast.success('Code verified! Please set your new password.');
        setStep('password');
      } else {
        customToast.error(response.message || 'Invalid verification code');
      }
    } catch (error) {
      customToast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password.length < 8) {
      customToast.error('Password must be at least 8 characters long');
      return;
    }

    if (password !== confirmPassword) {
      customToast.error('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await authService.resetPassword(email, code, password, confirmPassword);
      
      if (response.success) {
        customToast.success('Password reset successful! Please login with your new password.');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        customToast.error(response.message || 'Failed to reset password');
      }
    } catch (error) {
      customToast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (timer > 0) return;
    
    setLoading(true);
    try {
      const response = await authService.sendResetCode(email);
      if (response.success) {
        customToast.success('New verification code sent!');
        setTimer(900);
        setCode('');
      } else {
        customToast.error(response.message || 'Failed to send code');
      }
    } catch (error) {
      customToast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className={`min-h-screen w-full overflow-x-auto py-6 sm:py-8 lg:py-12 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${
      theme === 'dark' 
        ? 'bg-slate-900' 
        : 'bg-white'
    }`}>
      {/* Theme Toggle - Top Right */}
      <button
        onClick={toggleTheme}
        className={`fixed top-4 right-4 z-50 p-2 sm:p-3 rounded-xl shadow-lg transition-all duration-300 ${
          theme === 'dark'
            ? 'bg-slate-800 hover:bg-slate-700 text-yellow-400'
            : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-200'
        }`}
        aria-label="Toggle theme"
      >
        {theme === 'dark' ? <Sun size={18} className="sm:w-5 sm:h-5" /> : <Moon size={18} className="sm:w-5 sm:h-5" />}
      </button>

      <div className="min-h-[calc(100vh-8rem)] flex items-center justify-center">
        <div className="w-full max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-4 sm:mb-6">
            <h1 className={`text-lg sm:text-xl lg:text-2xl font-bold mb-1 sm:mb-2 ${
              theme === 'dark' ? 'text-white' : 'text-gray-800'
            }`}>
              {step === 'email' && 'Forgot Password?'}
              {step === 'code' && `Welcome back${userName ? ', ' + userName : ''}!`}
              {step === 'password' && 'Reset Password'}
            </h1>
            <p className={`text-xs sm:text-sm ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {step === 'email' && 'Enter your email to receive a verification code'}
              {step === 'code' && 'Enter the 6-digit code sent to your email'}
              {step === 'password' && 'Create a new password for your account'}
            </p>
          </div>

          {/* Card */}
          <div className={`rounded-xl sm:rounded-2xl shadow-xl sm:shadow-2xl border p-4 sm:p-6 lg:p-8 relative overflow-hidden ${
            theme === 'dark'
              ? 'bg-slate-800 border-slate-700'
              : 'bg-white border-gray-200'
          }`}>
            {/* Top border accent */}
            <div className="absolute top-0 left-0 w-full h-1 bg-indigo-600"></div>

            {/* Step Indicator */}
            <div className="flex justify-center mb-4 sm:mb-6">
              <div className="flex items-center space-x-2 sm:space-x-4">
                <div className={`flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full font-semibold text-sm ${
                  step === 'email' 
                    ? 'bg-indigo-600 text-white' 
                    : 'bg-green-500 text-white'
                }`}>
                  {step === 'email' ? <Mail size={16} /> : '✓'}
                </div>
                <div className={`w-12 sm:w-16 h-1 rounded ${
                  step === 'code' || step === 'password' ? 'bg-green-500' : theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                }`}></div>
                <div className={`flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full font-semibold text-sm ${
                  step === 'code' 
                    ? 'bg-indigo-600 text-white' 
                    : step === 'password'
                    ? 'bg-green-500 text-white'
                    : theme === 'dark' ? 'bg-slate-600 text-gray-400' : 'bg-gray-300 text-gray-600'
                }`}>
                  {step === 'password' ? '✓' : <Shield size={16} />}
                </div>
                <div className={`w-12 sm:w-16 h-1 rounded ${
                  step === 'password' ? 'bg-green-500' : theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                }`}></div>
                <div className={`flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full font-semibold text-sm ${
                  step === 'password' ? 'bg-indigo-600 text-white' : theme === 'dark' ? 'bg-slate-600 text-gray-400' : 'bg-gray-300 text-gray-600'
                }`}>
                  <Lock size={16} />
                </div>
              </div>
            </div>

            {/* Email Step */}
            {step === 'email' && (
              <form onSubmit={handleSendCode} className="space-y-5">
                <div>
                  <label htmlFor="email" className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Email Address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`w-full px-4 py-3 border-2 rounded-xl text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 transition-all duration-300 ${
                      theme === 'dark'
                        ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-400'
                        : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                    }`}
                    placeholder="your@email.com"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  fullWidth
                  loading={loading}
                  loadingText="Sending code..."
                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3.5 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 text-sm sm:text-base"
                >
                  Send Verification Code
                </Button>
              </form>
            )}

            {/* Code Verification Step */}
            {step === 'code' && (
              <form onSubmit={handleVerifyCode} className="space-y-5">
                <div>
                  <label htmlFor="code" className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Verification Code
                  </label>
                  <input
                    id="code"
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className={`w-full px-4 py-3 border-2 rounded-xl text-center text-2xl font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 transition-all duration-300 ${
                      theme === 'dark'
                        ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-400'
                        : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                    }`}
                    placeholder="000000"
                    maxLength={6}
                    required
                  />
                  <p className={`mt-2 text-xs text-center ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    Code sent to {email}
                  </p>
                </div>

                {timer > 0 && (
                  <div className="text-center">
                    <p className={`text-sm ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      Code expires in <span className="font-semibold text-indigo-600">{formatTime(timer)}</span>
                    </p>
                  </div>
                )}

                <Button
                  type="submit"
                  fullWidth
                  loading={loading}
                  loadingText="Verifying..."
                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3.5 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 text-sm sm:text-base"
                >
                  Verify Code
                </Button>

                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={timer > 0 || loading}
                  className={`w-full text-sm font-medium transition-colors ${
                    timer > 0 || loading
                      ? theme === 'dark' ? 'text-gray-600 cursor-not-allowed' : 'text-gray-400 cursor-not-allowed'
                      : 'text-indigo-600 hover:text-indigo-700'
                  }`}
                >
                  Didn't receive the code? {timer > 0 ? `Resend in ${formatTime(timer)}` : 'Resend'}
                </button>
              </form>
            )}

            {/* Password Reset Step */}
            {step === 'password' && (
              <form onSubmit={handleResetPassword} className="space-y-5">
                <div>
                  <label htmlFor="password" className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    New Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`w-full px-4 py-3 border-2 rounded-xl text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 transition-all duration-300 ${
                      theme === 'dark'
                        ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-400'
                        : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                    }`}
                    placeholder="Enter new password"
                    minLength={8}
                    required
                  />
                  <p className={`mt-1 text-xs ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}>Minimum 8 characters</p>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className={`block text-sm font-semibold mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Confirm Password
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`w-full px-4 py-3 border-2 rounded-xl text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 transition-all duration-300 ${
                      theme === 'dark'
                        ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-400'
                        : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                    }`}
                    placeholder="Confirm new password"
                    minLength={8}
                    required
                  />
                </div>

                <Button
                  type="submit"
                  fullWidth
                  loading={loading}
                  loadingText="Resetting password..."
                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3.5 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 text-sm sm:text-base"
                >
                  Reset Password
                </Button>
              </form>
            )}

            {/* Back to Login */}
            <div className="mt-6 text-center">
              <Link
                to="/login"
                className={`inline-flex items-center gap-2 text-sm font-medium transition-colors ${
                  theme === 'dark'
                    ? 'text-gray-400 hover:text-indigo-400'
                    : 'text-gray-600 hover:text-indigo-600'
                }`}
              >
                <ArrowLeft size={16} />
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
